import { publicEnv } from '@/lib/config/public-env';
// Core dependencies
import { getSession, setGlobalSession } from '@/providers/enhanced-session';
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios, { AxiosHeaders } from 'axios';

// Internal utilities
import { EVENTS, tracking } from '@/lib/features/tracking';
import {
    guardViewAsWrite,
    recordViewAsWrite,
    type GuardedWrite,
} from '@/lib/view-as/backstop-shared';
import {
    isViewAsWriteRefusedError,
    normaliseViewAsPath,
    type ViewAsWriteClient,
} from '@/lib/view-as/write-policy';
import {
    authorizationFor,
    isTrustedAuthorization,
    navigateTo,
    reloadPage,
    resolveAuthRejection,
    sharedSessionProbe,
    signInUrlFor,
    trustAuthorization,
} from './auth-rejection';
import {
    ApiDenialError,
    isDenialReason,
    isSessionExpiredDenial,
    parseDenialEnvelope,
} from './denial';

export { ApiDenialError, isSessionExpiredDenial, parseDenialEnvelope } from './denial';
export type { DenialEnvelope, DenialReason } from './denial';

/**
 * Extended Axios request configuration interface that includes
 * custom metadata for tracking request timing
 */
interface RequestConfigWithMetadata extends InternalAxiosRequestConfig {
    metadata?: {
        startTime: number; // Timestamp when request started
        sentAsUserId?: string; // Who the cached session said was signed in
    };
    /** Set on the one retry a refused request gets (BT-2060). */
    authRetried?: boolean;
    /** The View-as backstop's decision for this request, recorded on a 2xx (M4S-5670 §6). */
    viewAsWrite?: GuardedWrite | null;
}

/** The axios instances' View-as backstop clients. */
type AxiosViewAsClient = Extract<ViewAsWriteClient, 'portal' | 'platform' | 'microservice'>;

/** An axios instance's base URL path (`/studio/v1` for the portal), which its request paths are relative to. */
function basePathOf(baseURL: string): string {
    try {
        return new URL(baseURL).pathname;
    } catch {
        return normaliseViewAsPath(baseURL);
    }
}

const DEFAULT_TIMEOUT = process.env.NODE_ENV === 'development' ? 30_000 : 300_000;

/** For a session that has to sign in again, and for development, which never asks. */
const SIGN_IN_AGAIN = {
    401: 'Unauthorized: Please sign in again.',
    403: 'Session expired. Please sign in again.',
} as const;

/** For an API that refused a live session. DANI's report page looks for "access denied". */
const REFUSED = {
    401: 'This request was not authorized.',
    403: 'Access denied.',
} as const;

function statusError(message: string, status: number): Error & { status: number } {
    return Object.assign(new Error(message), { status });
}

/**
 * A 401 or 403 in a production browser build (BT-2060). The auth server decides:
 * only a session it no longer has goes to sign-in, a stale token gets one retry,
 * and any other refusal just fails the call. The rule is `resolveAuthRejection`
 * in ./auth-rejection.
 */
async function handleAuthRejection(
    instance: AxiosInstance,
    error: AxiosError,
    status: 401 | 403,
): Promise<AxiosResponse> {
    const config = error.config as RequestConfigWithMetadata | undefined;
    const sent = config ? AxiosHeaders.from(config.headers).get('Authorization') : undefined;
    const sentAuthorization = typeof sent === 'string' ? sent : undefined;

    // The auth server confirmed this very token moments ago, so the API is
    // refusing a live session. Don't ask again on every refused call.
    if (isTrustedAuthorization(sentAuthorization)) {
        throw statusError(REFUSED[status], status);
    }

    const probe = await sharedSessionProbe();
    const action = resolveAuthRejection({
        probe,
        status,
        sentAuthorization,
        sentAsUserId: config?.metadata?.sentAsUserId,
        alreadyRetried: Boolean(config?.authRetried),
    });

    if (action === 'sign-in') {
        const signInUrl = signInUrlFor(window.location);
        if (signInUrl) navigateTo(signInUrl);
        throw statusError(SIGN_IN_AGAIN[status], status);
    }
    if (action === 'reload') {
        reloadPage();
        throw statusError('Signed in as someone else. Reloading the page.', status);
    }
    if (probe.state === 'live') {
        // Same person: their freshest tokens serve the retry and every later request.
        setGlobalSession(probe.session);
        trustAuthorization(authorizationFor(probe.session));
        if (action === 'retry' && config) {
            config.authRetried = true;
            return instance.request(config);
        }
    }
    throw statusError(REFUSED[status], status);
}

/**
 * Creates and configures a custom Axios instance with interceptors for authentication,
 * error handling, and performance tracking
 *
 * @param baseURL - The base URL for all requests made with this instance
 * @param viewAsClient - Which View-as backstop client this instance is (M4S-5670 §6)
 * @returns Configured Axios instance
 */
const createAxiosInstance = (baseURL: string, viewAsClient: AxiosViewAsClient) => {
    const viewAsBasePath = basePathOf(baseURL);

    // Create new Axios instance with default configuration
    const instance = axios.create({
        baseURL,
        timeout: DEFAULT_TIMEOUT,
        headers: {
            'X-MFour-Studio-Internal': 'true', // Identify requests as internal
        },
        withCredentials: true, // Enable sending cookies with cross-origin requests
    });

    // Add request interceptor to handle authentication and request timing
    instance.interceptors.request.use(
        async (config: RequestConfigWithMetadata) => {
            // View-as write backstop (M4S-5670 §6). A no-op unless the tab holds a
            // View-as session. A refused write rejects here with
            // ViewAsWriteRefusedError and never leaves the tab.
            config.viewAsWrite = await guardViewAsWrite({
                client: viewAsClient,
                method: config.method ?? 'GET',
                path: normaliseViewAsPath(config.url ?? '', viewAsBasePath),
                body: config.data,
            });

            // Track API request event
            tracking.event(EVENTS.STUDIO.API_REQUEST, {
                method: config.method?.toUpperCase(),
                url: config.url,
            });

            // Add authentication token from session (now uses cached session - no API fetch)
            const clientSession = await getSession();
            if (clientSession?.idToken) {
                config.headers['Authorization'] = `Bearer ${clientSession.idToken}`;
            }

            // Ensure Content-Type is set for JSON requests and not for FormData
            if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
                config.headers['Content-Type'] = 'application/json';
            }

            // Store request start time for performance tracking, and who it was sent as
            config.metadata = { startTime: Date.now(), sentAsUserId: clientSession?.user?.id };

            return config;
        },
        (error) => {
            // Track any errors that occur during request setup
            tracking.error(error, {
                type: 'axios_request_error',
                url: error.config?.url,
                method: error.config?.method,
            });
            return Promise.reject(error);
        },
    );

    // Add response interceptor to handle response tracking and error cases
    instance.interceptors.response.use(
        (response) => {
            // Track API response event
            tracking.event(EVENTS.STUDIO.API_RESPONSE, {
                method: response.config.method?.toUpperCase(),
                url: response.config.url,
                status: response.status,
            });

            // Calculate request duration for successful responses
            const config = response.config as RequestConfigWithMetadata;
            const duration = Date.now() - (config.metadata?.startTime || 0);

            // Track successful API call metrics
            tracking.api(response.config.url || 'unknown', duration, response.status, true);

            // Record an allowed View-as write or export now that it succeeded.
            recordViewAsWrite(config.viewAsWrite, response.status);

            return response;
        },
        (error) => {
            // A write the View-as backstop refused never left the tab: hand it to
            // the caller as-is, with no error tracking, sign-in or reload.
            if (isViewAsWriteRefusedError(error)) {
                return Promise.reject(error);
            }

            // Track error with source for better debugging
            tracking.errorWithSource(error as Error, 'axios.instance.response');

            // Calculate request duration even for failed requests
            const config = error.config as RequestConfigWithMetadata;
            const duration = Date.now() - (config?.metadata?.startTime || 0);

            // Track failed API call metrics
            if (error.config) {
                tracking.api(
                    error.config.url || 'unknown',
                    duration,
                    error.response?.status || 0,
                    false,
                );
            }

            // Track detailed error information
            tracking.error(error, {
                type: 'axios_response_error',
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                duration,
            });

            if (error.code === 'ECONNABORTED') {
                return Promise.reject(new Error('Request timed out. Please try again.'));
            }

            if (!error.response) {
                return Promise.reject(
                    new Error(
                        'Network error: Unable to reach the server. Please check your connection.',
                    ),
                );
            }

            // Handle specific HTTP status codes
            switch (error.response.status) {
                case 401:
                case 403: {
                    const status = error.response.status as 401 | 403;
                    // A credits denial (NO_CLAIM and the rest) stays on the route as a
                    // typed error. Session loss is decided by the auth server (BT-2060).
                    if (status === 403) {
                        const envelope = parseDenialEnvelope(error.response?.data);
                        if (isDenialReason(envelope.reason) && !isSessionExpiredDenial(envelope)) {
                            return Promise.reject(new ApiDenialError(envelope, 'Forbidden'));
                        }
                    }
                    // Only a production browser build ever navigates, and only once the
                    // auth server says the session is gone (BT-2060). In development/test
                    // the session provider handles expired sessions; external APIs may
                    // return 401 even with valid local auth, which would cause false
                    // redirects in E2E tests.
                    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
                        return handleAuthRejection(instance, error, status);
                    }
                    return Promise.reject(statusError(SIGN_IN_AGAIN[status], status));
                }
                case 404: {
                    // Handle not found
                    return Promise.reject(new Error('Resource not found.'));
                }
                case 500: {
                    // Handle server error
                    return Promise.reject(
                        new Error(error.response?.data?.message || 'Internal server error.'),
                    );
                }
                default: {
                    const apiMessage = error.response?.data?.message;
                    return Promise.reject(new Error(apiMessage || error.message));
                }
            }
        },
    );

    return instance;
};

// Export configured API instances for different endpoints
export const studioApi = createAxiosInstance(
    `${publicEnv('NEXT_PUBLIC_PLATFORM_API_BASE_ENDPOINT')}/api`,
    'platform',
);
export const portalApi = createAxiosInstance(
    `${publicEnv('NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT')}/studio/v1`,
    'portal',
);
export const microserviceApi = createAxiosInstance(
    `${publicEnv('NEXT_PUBLIC_MICROSERVICE_API_BASE_ENDPOINT')}`,
    'microservice',
);
